// src/components/vendor/VendorRegistration.tsx
import React, { useState } from 'react';
import { 
  Box, 
  Button, 
  Container, 
  TextField, 
  Typography, 
  Paper, 
  Link,
  Alert,
  CircularProgress,
  useTheme
} from '@mui/material';
import { Formik, Form, Field, ErrorMessage } from 'formik';
import * as Yup from 'yup';
import { useNavigate } from 'react-router-dom';
import StoreIcon from '@mui/icons-material/Store';

const VendorRegistrationSchema = Yup.object().shape({
  email: Yup.string()
    .email('Invalid email address')
    .required('Email is required'),
  businessName: Yup.string()
    .required('Business name is required')
    .min(2, 'Business name must be at least 2 characters'),
  contactPerson: Yup.string()
    .required('Contact person name is required'),
  phone: Yup.string()
    .required('Phone number is required')
    .matches(/^[0-9+\-() ]+$/, 'Invalid phone number format'),
});

const initialValues = {
  email: '',
  businessName: '',
  contactPerson: '',
  phone: '',
};

const VendorRegistration: React.FC = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');

  const handleSubmit = async (values: typeof initialValues, { setSubmitting }: any) => {
    setSubmitStatus('loading');
    try {
      console.log('Form values submitted:', values);
      await new Promise(resolve => setTimeout(resolve, 1500));
      setSubmitStatus('success');
      setTimeout(() => navigate('/vendor/onboarding'), 2000);
    } catch (error) {
      console.error('Error submitting form:', error);
      setSubmitStatus('error');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Container maxWidth="md" sx={{ py: 8 }}>
      <Box sx={{ 
        textAlign: 'center', 
        mb: 8,
        '& svg': {
          backgroundColor: theme.palette.primary.main,
          color: theme.palette.primary.contrastText,
          p: 2,
          borderRadius: '16px',
          boxShadow: theme.shadows[2]
        }
      }}>
        <StoreIcon sx={{ fontSize: 64, mb: 3 }} />
        <Typography variant="h2" component="h1" gutterBottom sx={{ 
          fontWeight: 700,
          letterSpacing: '-0.02em'
        }}>
          Join Our Delivery Network
        </Typography>
        <Typography variant="h6" color="text.secondary">
          Expand your business reach with our trusted delivery platform
        </Typography>
      </Box>

      <Paper 
        sx={{
          p: 6,
          borderRadius: 4,
          boxShadow: theme.shadows[4],
          background: theme.palette.background.paper,
          border: `1px solid ${theme.palette.divider}`
        }}
      >
        <Formik initialValues={initialValues} validationSchema={VendorRegistrationSchema} onSubmit={handleSubmit}>
          {({ errors, touched, isSubmitting }) => (
            <Form>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                <Typography variant="h5" component="h2" sx={{ 
                  fontWeight: 600,
                  pb: 2,
                  borderBottom: `2px solid ${theme.palette.divider}`
                }}>
                  Business Details
                </Typography>

                <Field
                  as={TextField}
                  fullWidth
                  label="Business Name"
                  name="businessName"
                  variant="outlined"
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2,
                      '& fieldset': {
                        borderColor: theme.palette.divider,
                      },
                      '&:hover fieldset': {
                        borderColor: theme.palette.primary.main,
                      },
                      '&.Mui-focused fieldset': {
                        borderColor: theme.palette.primary.main,
                        borderWidth: 2
                      }
                    }
                  }}
                  error={touched.businessName && Boolean(errors.businessName)}
                  helperText={touched.businessName && errors.businessName}
                />

                <Box sx={{ display: 'flex', gap: 4, flexDirection: { xs: 'column', md: 'row' } }}>
                  <Field
                    as={TextField}
                    fullWidth
                    label="Contact Person"
                    name="contactPerson"
                    variant="outlined"
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        borderRadius: 2,
                        '& fieldset': {
                          borderColor: theme.palette.divider,
                        },
                        '&:hover fieldset': {
                          borderColor: theme.palette.primary.main,
                        },
                        '&.Mui-focused fieldset': {
                          borderColor: theme.palette.primary.main,
                          borderWidth: 2
                        }
                      }
                    }}
                    error={touched.contactPerson && Boolean(errors.contactPerson)}
                    helperText={touched.contactPerson && errors.contactPerson}
                  />

                  <Field
                    as={TextField}
                    fullWidth
                    label="Phone Number"
                    name="phone"
                    variant="outlined"
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        borderRadius: 2,
                        '& fieldset': {
                          borderColor: theme.palette.divider,
                        },
                        '&:hover fieldset': {
                          borderColor: theme.palette.primary.main,
                        },
                        '&.Mui-focused fieldset': {
                          borderColor: theme.palette.primary.main,
                          borderWidth: 2
                        }
                      }
                    }}
                    error={touched.phone && Boolean(errors.phone)}
                    helperText={touched.phone && errors.phone}
                  />
                </Box>

                <Field
                  as={TextField}
                  fullWidth
                  label="Email Address"
                  name="email"
                  type="email"
                  variant="outlined"
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2,
                      '& fieldset': {
                        borderColor: theme.palette.divider,
                      },
                      '&:hover fieldset': {
                        borderColor: theme.palette.primary.main,
                      },
                      '&.Mui-focused fieldset': {
                        borderColor: theme.palette.primary.main,
                        borderWidth: 2
                      }
                    }
                  }}
                  error={touched.email && Boolean(errors.email)}
                  helperText={touched.email && errors.email}
                />

                {(submitStatus === 'success' || submitStatus === 'error') && (
                  <Alert severity={submitStatus} sx={{ 
                    borderRadius: 2,
                    alignItems: 'center',
                    '& .MuiAlert-message': { py: 1 }
                  }}>
                    {submitStatus === 'success' 
                      ? 'Registration successful! Redirecting...'
                      : 'Registration error. Please try again.'}
                  </Alert>
                )}

                <Box sx={{ 
                  display: 'flex', 
                  flexDirection: { xs: 'column', md: 'row' }, 
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  gap: 2,
                  pt: 4
                }}>
                  <Typography variant="body2" color="text.secondary">
                    Existing partner? <Link href="/vendor/login" color="primary" sx={{ fontWeight: 500 }}>Sign in</Link>
                  </Typography>
                  
                  <Button
                    type="submit"
                    variant="contained"
                    size="large"
                    disabled={isSubmitting || ['loading', 'success'].includes(submitStatus)}
                    sx={{
                      px: 6,
                      py: 1.5,
                      borderRadius: 2,
                      textTransform: 'none',
                      fontSize: '1rem',
                      fontWeight: 600,
                      transition: 'all 0.3s ease',
                      '&:hover': {
                        transform: 'translateY(-1px)',
                        boxShadow: theme.shadows[4]
                      }
                    }}
                  >
                    {submitStatus === 'loading' ? (
                      <CircularProgress size={24} color="inherit" />
                    ) : (
                      'Apply Now'
                    )}
                  </Button>
                </Box>
              </Box>
            </Form>
          )}
        </Formik>
      </Paper>

      <Box sx={{ 
        textAlign: 'center', 
        mt: 4,
        '& .MuiLink-root': {
          fontWeight: 500,
          '&:hover': { textDecoration: 'none' }
        }
      }}>
        <Typography variant="body2" color="text.secondary">
          By applying, you agree to our{' '}
          <Link href="/terms" color="primary">Terms</Link> and{' '}
          <Link href="/privacy" color="primary">Privacy Policy</Link>
        </Typography>
      </Box>
    </Container>
  );
};

export default VendorRegistration;